import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Container, Row, Col, Nav, Alert, Button, Table, Modal } from 'react-bootstrap';
import Select from 'react-select';
import Breadcrumbs from '../../Common/Breadcrumbs';
import ShimmerLoader from '../../Common/ShimmerLoader';
import { PermissionWrapper } from '../../components/PermissionWrapper/PermissionWrapper';

import {
  getUpcomingManageTournaments,
  getPastManageTournaments,
  getCancelledManageTournaments,
  deleteTournament,
  ManageTournament,
  formatDate,
  getAgeCategoriesDisplay,
} from '../../api/tournament/manageTournamentService';
import {
  hasAnyPermission,
  hasTournamentWriteAccess,
  type User,
} from '../../utils/roleBasedAccess';
import './ManageTournaments.css';

type TournamentStatus = 'upcoming' | 'past' | 'cancelled';

const ManageTournaments = () => {
  const navigate = useNavigate();
  const { user, role, permissions } = useSelector((state: any) => state.Login);
  
  // State management
  const [activeTab, setActiveTab] = useState<TournamentStatus>('upcoming');
  const [tournaments, setTournaments] = useState<ManageTournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [isPageLoading, setIsPageLoading] = useState(false);
  
  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [tournamentToDelete, setTournamentToDelete] = useState<ManageTournament | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteReason, setDeleteReason] = useState<string>('');

  // Delete reason options
  const deleteReasonOptions = [
    { value: 'Scheduling conflict', label: 'Scheduling conflict' },
    { value: 'Showers', label: 'Showers' },
    { value: 'Weather conditions', label: 'Weather conditions' },
    { value: 'Venue unavailability', label: 'Venue unavailability' },
    { value: 'Low participation', label: 'Low participation' },
    { value: 'Technical issues', label: 'Technical issues' },
    { value: 'Other', label: 'Other' }
  ];

  // Check if user has access
  const userData: User = {
    ...(user || {}),
    subRole: role,
    permissions: permissions,
  };

  const isAdminUser = hasAnyPermission(userData, [
    'tournament.admin',
    'tournament.manage',
    'tournament.view',
    'tournament.read',
    'tournament.registrations.view',
    'tournament.registrations.manage',
    'tournament.tournaments.view',
  ]);

  // Write access for tournaments (edit/delete)
  const canManageTournaments = hasTournamentWriteAccess(userData, [
    'tournament.tournaments.manage',
  ]);

  useEffect(() => {
    if (!isAdminUser) {
      setLoading(false);
      return;
    }
    loadData();
  }, [isAdminUser]);

  useEffect(() => {
    if (isAdminUser) {
      loadTournaments();
    }
  }, [activeTab, currentPage, isAdminUser]);

  const loadData = async () => {
    try {
      setLoading(true);
      await loadTournaments();
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to determine tournament status for sorting
  const getTournamentStatusForSorting = (tournament: ManageTournament): 'ongoing' | 'upcoming' | 'completed' => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for accurate comparison
    
    const startDate = new Date(tournament.startDate);
    startDate.setHours(0, 0, 0, 0);
    
    const endDate = new Date(tournament.endDate);
    endDate.setHours(23, 59, 59, 999); // Set to end of day
    
    // If tournament is cancelled, treat it as completed for sorting
    if (tournament.status === 'cancelled' || tournament.cancellationInfo) {
      return 'completed';
    }
    
    // Ongoing: current date is between start and end date
    if (today >= startDate && today <= endDate) {
      return 'ongoing';
    }
    
    // Upcoming: start date is in the future
    if (today < startDate) {
      return 'upcoming';
    }
    
    // Completed: end date is in the past
    return 'completed';
  };

  // Helper function to sort tournaments by status priority: ongoing > upcoming > completed
  const sortTournamentsByStatus = (tournaments: ManageTournament[]): ManageTournament[] => {
    const statusPriority: { [key: string]: number } = {
      'ongoing': 1,
      'upcoming': 2,
      'completed': 3
    };
    
    return [...tournaments].sort((a, b) => {
      const statusA = getTournamentStatusForSorting(a);
      const statusB = getTournamentStatusForSorting(b);
      
      // First, sort by status priority
      const priorityDiff = statusPriority[statusA] - statusPriority[statusB];
      if (priorityDiff !== 0) {
        return priorityDiff;
      }
      
      // If same status, sort by start date (earliest first for ongoing/upcoming, latest first for completed)
      const dateA = new Date(a.startDate).getTime();
      const dateB = new Date(b.startDate).getTime();
      
      if (statusA === 'completed') {
        // For completed tournaments, show most recent first
        return dateB - dateA;
      } else {
        // For ongoing and upcoming, show earliest first
        return dateA - dateB;
      }
    });
  };

  const loadTournaments = async () => {
    try {
      setIsPageLoading(true);
      setError(null);
      
      let result;
      switch (activeTab) {
        case 'upcoming':
          result = await getUpcomingManageTournaments(currentPage, 10);
          break;
        case 'past':
          result = await getPastManageTournaments(currentPage, 10);
          break;
        case 'cancelled':
          result = await getCancelledManageTournaments(currentPage, 10);
          break;
        default:
          result = await getUpcomingManageTournaments(currentPage, 10);
      }
      
      // Sort tournaments by status: ongoing > upcoming > completed
      const sortedTournaments = sortTournamentsByStatus(result.tournaments);
      
      setTournaments(sortedTournaments);
      setTotalCount(result.totalCount);
      setTotalPages(Math.ceil(result.totalCount / 10));
      
    } catch (error: any) {
      console.error('Error loading tournaments:', error);
      setError(error.message || 'Failed to load tournaments');
      // Clear tournaments array when there's an error to avoid showing stale data
      setTournaments([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setIsPageLoading(false);
    }
  };

  const handleTabClick = (tabName: TournamentStatus) => {
    setActiveTab(tabName);
    setCurrentPage(1);
    setError(null); // Clear any existing error when switching tabs
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleViewTournament = (tournamentId: string) => {
    navigate(`/manage-tournaments/${tournamentId}`);
  };

  const handleEditTournament = (tournamentId: string) => {
    navigate(`/manage-tournaments/${tournamentId}`, { 
      state: { isEditMode: true } 
    });
  };

  const handleDeleteTournament = (tournament: ManageTournament) => {
    setTournamentToDelete(tournament);
    setDeleteReason(''); // Reset reason when opening modal
    setDeleteError(null);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!tournamentToDelete) return;
    
    if (!deleteReason.trim()) {
      setDeleteError('Please select a reason for deletion');
      return;
    }
    
    setIsDeleting(true);
    setDeleteError(null);
    
    try {
      console.log('Deleting tournament:', tournamentToDelete._id);
      console.log('Delete reason:', deleteReason);
      
      await deleteTournament(tournamentToDelete._id, { reason: deleteReason });
      
      setShowDeleteModal(false);
      setTournamentToDelete(null);
      setDeleteReason('');
      
      // Reload tournaments after successful deletion
      await loadTournaments();
    } catch (error: any) {
      setDeleteError(error.message || 'Failed to delete tournament');
    } finally {
      setIsDeleting(false);
    }
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setTournamentToDelete(null);
    setDeleteError(null);
    setDeleteReason('');
  };

  const getTabTitle = () => {
    switch (activeTab) {
      case 'upcoming':
        return 'Upcoming Tournaments';
      case 'past':
        return 'Past Tournaments';
              case 'cancelled':
          return 'Cancelled Tournaments';
      default:
        return 'Tournaments';
    }
  };

  const breadcrumbItems = [
    { label: 'Manage Tournaments', onClick: () => navigate('/manage-tournaments') },
    { label: `${getTabTitle()} Listing`, active: true }
  ];

  return (
    <div className="manage-tournaments-page" style={{ marginTop: '20px' }}>
      <Container fluid>
        {/* Breadcrumbs - Left aligned like other modules */}
        <div className="mb-3" style={{ padding: '0 15px' }}>
          <Breadcrumbs items={breadcrumbItems} />
        </div>

        <Row>
          <Col>
            <div className="tournaments-container">
              {/* Main Tabs */}
              <Nav variant="tabs" className="tournament-tabs mb-4">
                <Nav.Item>
                  <Nav.Link 
                    className="tournament-tab"
                    active={activeTab === 'upcoming'}
                    onClick={() => handleTabClick('upcoming')}
                  >
                    Upcoming Tournaments
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link 
                    className="tournament-tab"
                    active={activeTab === 'past'}
                    onClick={() => handleTabClick('past')}
                  >
                    Past Tournaments
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link 
                    className="tournament-tab"
                    active={activeTab === 'cancelled'}
                    onClick={() => handleTabClick('cancelled')}
                  >
                    Cancelled Tournaments
                  </Nav.Link>
                </Nav.Item>
              </Nav>

              {/* Tab Content Container */}
              <div className="tab-content-container">
                {/* Title Section */}
                <div className="tournament-controls mb-4">
                  <Row className="align-items-center">
                    <Col md={6}>
                      <div className="tab-title-section">
                        <h3 className="tab-title">{getTabTitle()}</h3>
                      </div>
                    </Col>
                    <Col md={6}>
                      <div className="filter-controls">
                        {/* Add New button hidden as per user request */}
                        {/* <Button 
                          variant="dark" 
                          className="add-new-btn"
                          onClick={() => navigate('/manage-tournaments/add', { 
                            state: { isAddMode: true } 
                          })}
                        >
                          Add New +
                        </Button> */}
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* Error Alert */}
                {error && (
                  <Alert variant="danger" className="mb-4">
                    <Alert.Heading>Error Loading Tournaments</Alert.Heading>
                    <p>{error}</p>
                    <hr />
                    <button 
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => loadTournaments()}
                    >
                      Try Again
                    </button>
                  </Alert>
                )}

                {/* Tournament Table */}
                {loading || isPageLoading ? (
                  <div className="tournament-table-wrapper">
                    <ShimmerLoader type="table" />
                  </div>
                ) : tournaments.length > 0 ? (
                  <div className="tournament-table-wrapper">
                    <Table responsive className="tournament-table">
                      <thead>
                        <tr>
                          <th>Tournament Name</th>
                          <th>Sports</th>
                          <th>Age Category</th>
                          <th>Date</th>
                          <th>Venue</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tournaments.map((tournament) => (
                          <tr key={tournament._id}>
                            <td>
                              <div className="tournament-name">
                                <strong>{tournament.name}</strong>
                                <br />
                                <small className="text-muted">{tournament.shortName}</small>
                              </div>
                            </td>
                            <td>
                              <div className="tournament-sport">
                                {tournament.sports}
                              </div>
                            </td>
                            <td>
                              <div className="tournament-age-categories">
                                {getAgeCategoriesDisplay(tournament.ageCategories)}
                              </div>
                            </td>
                            <td>
                              <div className="tournament-date">
                                {formatDate(tournament.startDate)}
                                {tournament.endDate !== tournament.startDate && (
                                  <React.Fragment>
                                    <br />
                                    <small className="text-muted">to {formatDate(tournament.endDate)}</small>
                                  </React.Fragment>
                                )}
                              </div>
                            </td>
                            <td>
                              <div className="tournament-venue">
                                {tournament.location}
                              </div>
                            </td>
                            <td>
                              <div className="tournament-actions">
                                {/* Everyone with view-level access to this page can view */}
                                <button
                                  className="action-btn btn-view"
                                  onClick={() => handleViewTournament(tournament._id)}
                                  title="View Details"
                                >
                                  <i className="ri-eye-line"></i>
                                </button>
                                {/* Edit/Delete only for write access */}
                                {canManageTournaments && (
                                  <>
                                    <button
                                      className="action-btn btn-edit"
                                      onClick={() => handleEditTournament(tournament._id)}
                                      title="Edit Tournament"
                                    >
                                      <i className="ri-pencil-line"></i>
                                    </button>
                                    <button
                                      className="action-btn btn-delete"
                                      onClick={() => handleDeleteTournament(tournament)}
                                      title="Delete Tournament"
                                    >
                                      <i className="ri-delete-bin-line"></i>
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="pagination-wrapper">
                        <nav>
                          <ul className="pagination justify-content-center">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button 
                                className="page-link" 
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                              >
                                Previous
                              </button>
                            </li>
                            
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                              <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                <button 
                                  className="page-link" 
                                  onClick={() => handlePageChange(page)}
                                >
                                  {page}
                                </button>
                              </li>
                            ))}
                            
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                              <button 
                                className="page-link" 
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                              >
                                Next
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="no-tournaments">
                    <div className="empty-icon">
                      <i className="ri-trophy-line"></i>
                    </div>
                    <h4>No {activeTab === 'cancelled' ? 'cancelled' : activeTab} tournaments available</h4>
                    <p>
                      There are no {activeTab === 'cancelled' ? 'cancelled' : activeTab} tournaments at the moment.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </Container>

      {/* Custom Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={closeDeleteModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Tournament</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="text-center mb-4">
            <div className="text-danger mb-3">
              <i className="ri-delete-bin-line" style={{ fontSize: '3rem' }}></i>
            </div>
            <h5>Are you sure you want to Delete this tournament?</h5>
            <p className="text-muted">
              This action may affect teams and refund status.
            </p>
          </div>
          
          <div className="mb-4">
            <label className="form-label fw-semibold">Reason*</label>
            <Select
              classNamePrefix="react-select"
              value={deleteReasonOptions.find(option => option.value === deleteReason)}
              onChange={(selectedOption) => setDeleteReason(selectedOption?.value || '')}
              options={deleteReasonOptions}
              placeholder="Select a reason"
              isDisabled={isDeleting}
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '40px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  boxShadow: 'none'
                }),
                menu: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
          </div>
          
          {deleteError && (
            <div className="alert alert-danger" role="alert">
              <i className="ri-error-warning-line me-2"></i>
              {deleteError}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="light" 
            onClick={closeDeleteModal}
            disabled={isDeleting}
          >
            GoBack
          </Button>
          <Button 
            variant="danger" 
            onClick={confirmDelete}
            disabled={isDeleting || !deleteReason.trim()}
          >
            {isDeleting ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Deleting...
              </>
            ) : (
              'Yes, Cancel'
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ManageTournaments; 